// IngredientsTab.jsx
import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
    fetchIngredientSettings,
    updateIngredient,
    removeIngredient,
    addIngredient, getMenuAddons,
} from "../../api/api";

const getSortMeta = (code, settings) => {
    const sort = parseInt(code, 10);
    const isHidden = sort >= settings?.hidden?.min;
    const realSort = isHidden ? sort - settings?.hidden?.offset : sort;
    const [type, cat, idx] = realSort.toString().padStart(3, '0').split('');
    return {
        type,
        cat: parseInt(cat),
        idx: parseInt(idx),
        hidden: isHidden,
        sort: realSort,
        raw: sort
    };
};

const formatSort = ({ type, cat, idx, hidden, settings }) => {
    const base = `${type}${cat}${idx}`;
    const value = parseInt(base, 10);
    return (hidden ? value + settings.hidden.offset : value)
        .toString()
        .padStart(3, '0');
};

export default function IngredientsTab({ item, refresh, write }) {
    const [settings, setSettings] = useState({});
    const [ingredients, setIngredients] = useState([]);
    const [addOns, setAddons] = useState([])
    const [editedMap, setEditedMap] = useState({});
    const [newAddType, setNewAddType] = useState(null);

    useEffect(() => {
        fetchIngredientSettings().then((res) => {

            if (res.ok) setSettings(res.data);
        });
    }, []);

    useEffect(() => {
        const list = [...item.pbo.iga].sort((a, b) => a.p0sort.localeCompare(b.p0sort));
        getMenuAddons(item.mnuid).then((res) => {
            if(res.ok){
                const list = res?.data?.T?.filter(item=>(item.m0sort>=600)).map((item)=>({value: item?.m0katnr, label: item?.m0text.toSwe()}))
                setAddons(list)
            }
        })
        setIngredients(list);
        setEditedMap({});
    }, [item]);

    const grouped = useMemo(() => {
        const byType = {};
        ingredients.forEach((ing) => {
            const meta = getSortMeta(ing.p0sort, settings);
            const { type, cat, idx } = meta;
            if (!byType[type]) byType[type] = {};
            if (settings?.[type]?.hasSubtypes) {
                if (!byType[type][cat]) byType[type][cat] = { header: null, items: [] };
                if (idx === 0) byType[type][cat].header = { ...ing, _meta: meta };
                else byType[type][cat].items.push({ ...ing, _meta: meta });
            } else {
                if (!byType[type].all) byType[type].all = [];
                byType[type].all.push({ ...ing, _meta: meta });
            }
        });
        return byType;
    }, [ingredients, settings]);

    const handleFieldChange = (idx, field) => (e) => {
        const value = e.target?.value ?? e.value
        setIngredients((prev) => {
            const copy = [...prev];
            copy[idx] = { ...copy[idx], [field]: value };
            return copy;
        });
        setEditedMap((m) => ({ ...m, [idx]: true }));
    };

    const handleSortChange = (idx) => (e) => {
        const num = parseInt(e.target.value, 10) || 0;
        setIngredients((prev) => {
            const copy = [...prev];
            const old = copy[idx];
            const meta = getSortMeta(old.p0sort, settings);
            const newSort = formatSort({ type: meta.type, cat: meta.cat, idx: num, hidden: meta.hidden, settings });
            copy[idx] = { ...old, p0sort: newSort };
            return copy;
        });
        setEditedMap((m) => ({ ...m, [idx]: true }));
    };

    const toggleHidden = (idx) => {
        setIngredients(prev => {
            const copy = [...prev];
            const old = copy[idx];
            const meta = getSortMeta(old.p0sort, settings);
            const newSort = formatSort({ ...meta, hidden: !meta.hidden, settings });
            copy[idx] = { ...old, p0sort: newSort };
            return copy;
        });
        setEditedMap((m) => ({ ...m, [idx]: true }));
    };

    const saveAll = async () => {
        await Promise.all(
            Object.keys(editedMap).map((i) => updateIngredient(ingredients[i]))
        );
        setEditedMap({});
        refresh();
    };

    const removeOne = async (idx) => {
        const ing = ingredients[idx];
        const res = await removeIngredient(ing);
        if (res.removed) {
            setIngredients((prev) => prev.filter((_, i) => i !== idx));
             setEditedMap({});
        }
    };

    const removeCategory = async (type, cat) => {
        const toRemove = ingredients.filter((ing) => {
            const s = getSortMeta(ing.p0sort, settings);
            return s.type === type && s.cat === cat;
        });
        await Promise.all(toRemove.map((ing) => removeIngredient(ing)));
        setIngredients((prev) => prev.filter((ing) => {
            const s = getSortMeta(ing.p0sort, settings);
            return !(s.type === type && s.cat === cat);
        }));
        setEditedMap({});
    };

    const baseIngredient = {
        p0typ: "P",
        p0kstns: item.pbo.plu00.p0kstns,
        p0katnr: item.pbo.plu00.p0katnr,
        p0lopnr: "",
        p0ingnr: "",
        p0text: "",
        p0sort: "001",
    };

    const createNewIngredient = (sort, text = "") => ({
        ...baseIngredient,
        p0sort: sort,
        p0text: text,
    });

    const addSimple = async (type) => {
        const list = ingredients.filter((ing) => getSortMeta(ing.p0sort, settings).type === type);
        const nextIdx = list.length + 1;
        const newSort = formatSort({ type, cat: 0, idx: nextIdx, hidden: false, settings });
        const newIng = createNewIngredient(newSort);
        const res = await addIngredient(newIng);
        if (res.added) setIngredients((prev) => [...prev, res.item]);
    };

    const addCategory = async (type) => {
        const cats = Object.keys(grouped[type] || {}).map(Number);
        const nextCat = cats.length ? Math.max(...cats) + 1 : 1;
        const newSort = formatSort({ type, cat: nextCat, idx: 0, hidden: false, settings });
        const newIng = createNewIngredient(newSort, "Ny kategori");
        const res = await addIngredient(newIng);
        if (res.added) setIngredients((prev) => [...prev, res.item]);
    };

    const addSubItem = async (type, cat) => {
        const items = ingredients.filter((ing) => {
            const s = getSortMeta(ing.p0sort, settings);
            return s.type === type && s.cat === cat && s.idx > 0;
        });
        const nextIdx = items.length ? Math.max(...items.map((i) => getSortMeta(i.p0sort, settings).idx)) + 1 : 1;
        const newSort = formatSort({ type, cat, idx: nextIdx, hidden: false, settings });
        const newIng = createNewIngredient(newSort);
        const res = await addIngredient(newIng);
        if (res.added) setIngredients((prev) => [...prev, res.item]);
    };

    const typeOptions = Object.keys(settings || {}).filter((key)=>settings[key].label).map((key) => ({
        value: key,
        label: settings[key].label,
    }));

    const handleAdd = () => {
        if (!newAddType) return;
        const type = newAddType.value;
        settings[type]?.hasSubtypes ? addCategory(type) : addSimple(type);
        setNewAddType(null);
    };

    return (
        <div>
            {Object.entries(grouped).map(([type, group]) => (
                <section key={type} className="mb-5">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0">{settings?.[type]?.label}</h4>
                        {write && settings?.[type]?.hasSubtypes ? (
                            <button className="btn btn-outline-success" onClick={() => addCategory(type)}>
                                <i className="bi bi-plus-lg"></i> Ny kategori
                            </button>
                        ) : (
                            <button className="btn btn-outline-success mt-2" onClick={() => addSimple(type)}>
                                <i className="bi bi-plus-lg"></i> Lägg till {settings?.[type]?.label}
                            </button>
                        )}
                    </div>

                    {settings?.[type]?.hasSubtypes ? (
                        Object.entries(group).map(([cat, { header, items }]) => {
                            const catIdx = ingredients.findIndex((x) => x?.p0lopnr === header.p0lopnr);
                            const {_meta : {hidden}} = header
                            return (
                                <div key={cat} className={`mb-4 p-2 rounded ${hidden && "bg-danger-subtle"}`}>
                                    <div className={`d-flex align-items-center mb-2 gap-2`}>
                                        <button
                                            className={`btn ms-2 ${hidden ? 'btn-outline-danger' : 'btn-outline-secondary'}`}
                                            onClick={() => toggleHidden(catIdx)}
                                        >
                                            <i className={`bi ${hidden ? "bi-eye-slash" : "bi-eye"}`} />
                                        </button>
                                        <input
                                            value={header?.p0text || ""}
                                            onChange={handleFieldChange(catIdx, 'p0text')}
                                            className={`form-control flex-grow-1 fw-bold`}
                                            placeholder="Kategorinamn"
                                            disabled={!write}
                                        />
                                        {write && (
                                            <button
                                                className="btn btn-outline-danger"
                                                onClick={() => removeCategory(type, Number(cat))}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                    {items.map((ing) => {
                                        const idx = ingredients.findIndex((x) => x.p0lopnr === ing.p0lopnr);
                                        const {_meta : {hidden}} = ing
                                        return (
                                            <div key={ing.p0lopnr || ing.p0sort} className={`d-flex gap-2 p-2 rounded ms-4 ${hidden && "bg-danger-subtle"}`}>
                                                <button
                                                    className={`btn ms-2 ${hidden ? 'btn-outline-danger' : 'btn-outline-secondary'}`}
                                                    onClick={() => toggleHidden(idx)}
                                                >
                                                    <i className={`bi ${hidden ? "bi-eye-slash" : "bi-eye"}`}/>
                                                </button>
                                                <input
                                                    value={ing.p0text}
                                                    onChange={handleFieldChange(idx, 'p0text')}
                                                    className="form-control flex-grow-1"
                                                    disabled={!write}
                                                />
                                                {write && (
                                                    <button className="btn btn-outline-danger" onClick={() => removeOne(idx)}>
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {write && (
                                        <div className="d-flex ms-4 mb-2 mt-2">
                                            <button
                                                className={`btn ${hidden ? "btn-secondary" : "btn-outline-success"}`}
                                                onClick={() => addSubItem(type, Number(cat))}
                                            >
                                                <i className="bi bi-plus-lg"></i> Lägg till {settings?.[type]?.label}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        group.all?.map((ing) => {
                            const idx = ingredients.findIndex((x) => x.p0lopnr === ing.p0lopnr);
                            const {_meta: {hidden}} = ing;
                            const forceMatch = settings?.[type]?.matchObject
                            return (
                                <div key={ing.p0lopnr || ing.p0sort} className={`d-flex gap-2 p-2 roundedpm start ${hidden && "bg-danger-subtle"}`}>
                                    <button
                                        className={`btn ms-2 ${hidden ? 'btn-outline-danger' : 'btn-outline-secondary'}`}
                                        onClick={() => toggleHidden(idx)}
                                    >
                                        <i className={`bi ${hidden ? "bi-eye-slash" : "bi-eye"}`} />
                                    </button>
                                    {forceMatch ?
                                        <Select
                                            className="flex-grow-1"
                                            options={addOns}
                                            onChange={handleFieldChange(idx, 'p0text')}
                                            value={addOns.find(item=>(item.value == ing.p0text))}
                                        /> :
                                        <input
                                            value={ing.p0text}
                                            onChange={handleFieldChange(idx, 'p0text')}
                                            className="form-control flex-grow-1"
                                            disabled={!write}
                                        />
                                    }
                                    <input
                                        type="number"
                                        value={ing._meta.idx}
                                        onChange={handleSortChange(idx)}
                                        className="form-control w-25"
                                        disabled={!write}
                                    />
                                    {write && (
                                        <button className="btn btn-outline-danger" onClick={() => removeOne(idx)}>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    )}
                                </div>
                            );
                        })
                    )}
                </section>
            ))}

            {write && (
                <div className="d-flex gap-2 align-items-center mt-4">
                    <Select
                        placeholder="Välj typ att lägga till..."
                        options={typeOptions}
                        value={newAddType}
                        onChange={setNewAddType}
                        className="flex-grow-1"
                    />
                    <button
                        className="btn btn-secondary"
                        disabled={!newAddType}
                        onClick={handleAdd}
                    >
                        Lägg till
                    </button>
                </div>
            )}

            {write && Object.keys(editedMap).length > 0 && (
                <div className="text-end mt-3">
                    <button className="btn btn-success" onClick={saveAll}>
                        <i className="bi bi-save me-2"></i> Spara ändringar
                    </button>
                </div>
            )}
        </div>
    );
}
